/**
 * BOGOS free gift picker: stop it reopening on every page once the shopper says no.
 * BOGOS opens its picker on each page load while the cart qualifies and has no gift. After the
 * shopper closes it without adding the tee, keep it closed for 24 hours in every tab and on return
 * visits (localStorage, with a time stamp). The snooze also ends when the cart stops qualifying, so
 * crossing the threshold again opens it once more.
 * The unlocked tee row in the Boost cart drawer opens the picker on demand.
 *
 * Loaded on theme pages by snippets/bogos-boost-bridge.liquid, and on the Boost collections page by
 * the Tracking Script through the data-wayfx-bogos-extra scripts in layout/bogos-embed.liquid.
 */
(() => {
  if (window.__wayfxGiftSnooze) return;
  window.__wayfxGiftSnooze = true;

  const POPUP_ID = 'freegifts-main-popup-container';
  const OPEN = 'sca-is-visible-fg';
  const ITEM = '.bogos-offer-gift-item';
  const KEY = 'wayfx-gift-snoozed';
  const UNLOCKED_ROW = '#eTVNlhWbns-app .eTVNlhWbns__item-gift--unlocked';
  const SNOOZE_MS = 24 * 60 * 60 * 1000;

  // The value is the time of the dismissal; an older one has expired.
  const isActive = (value) => Date.now() - Number(value) < SNOOZE_MS;
  const storage = {
    get: () => {
      try {
        return isActive(localStorage.getItem(KEY));
      } catch (e) {
        return false;
      }
    },
    set: (on) => {
      try {
        on ? localStorage.setItem(KEY, String(Date.now())) : localStorage.removeItem(KEY);
      } catch (e) {
        // Storage blocked: the snooze just lasts for this page.
      }
    },
  };
  let snoozed = storage.get();
  const setSnoozed = (on) => {
    snoozed = on;
    storage.set(on);
  };
  // A dismissal or reset in another open tab applies here too.
  window.addEventListener('storage', (event) => {
    if (event.key === KEY) snoozed = isActive(event.newValue);
  });

  const styles = `
    ${UNLOCKED_ROW} { cursor: pointer; }
    ${UNLOCKED_ROW} .eTVNlhWbns__item-info-left::after {
      content: 'Choose your size'; display: block; margin-top: 2px; font-size: 13px;
      text-decoration: underline; text-underline-offset: 2px;
    }
  `;

  const watch = (popup) => {
    const hasGifts = () => popup.querySelectorAll(ITEM).length > 0;
    const isOpen = () => popup.classList.contains(OPEN);
    let manual = false; // Opened by the shopper from the drawer row.
    let hadGifts = hasGifts();

    // BOGOS toggles the class several times while it opens, so a class change is not a "no".
    // While snoozed, hide each automatic open before it paints.
    const onClass = () => {
      if (isOpen() && snoozed && !manual) popup.classList.remove(OPEN);
    };

    // Only the shopper's own dismissal counts: the close button, the backdrop, or Escape.
    const dismissed = () => {
      setSnoozed(true);
      manual = false;
    };
    popup.addEventListener('click', (event) => {
      if (!isOpen()) return;
      if (event.target === popup || event.target.closest('.bogos-slider-close-btn')) dismissed();
    }, true);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && isOpen()) dismissed();
    });

    // The cart stopped qualifying while on this page: crossing again counts as a new unlock.
    const onItems = () => {
      const now = hasGifts();
      if (hadGifts && !now) setSnoozed(false);
      hadGifts = now;
    };

    new MutationObserver(onClass).observe(popup, { attributes: true, attributeFilter: ['class'] });
    new MutationObserver(onItems).observe(popup, { childList: true, subtree: true });
    onClass();

    // On a new page the cart may no longer qualify: BOGOS finishes its check without drawing a gift
    // and without asking to show the picker.
    let check;
    document.addEventListener('bogos:gifts-end', () => {
      clearTimeout(check);
      check = setTimeout(() => {
        if (!hasGifts()) setSnoozed(false);
      }, 3000);
    });
    document.addEventListener('fg-gifts:show-slider', () => clearTimeout(check));

    // The unlocked tee row in the drawer opens the picker, snoozed or not.
    document.addEventListener('click', (event) => {
      if (!event.target.closest(UNLOCKED_ROW) || !hasGifts()) return;
      event.preventDefault();
      event.stopPropagation();
      manual = true;
      popup.classList.add(OPEN);
    }, true);
  };

  const start = () => {
    const style = document.createElement('style');
    style.textContent = styles;
    document.head.append(style);

    const popup = document.getElementById(POPUP_ID);
    if (popup) return watch(popup);
    const finder = new MutationObserver(() => {
      const found = document.getElementById(POPUP_ID);
      if (!found) return;
      finder.disconnect();
      watch(found);
    });
    finder.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => finder.disconnect(), 30000);
  };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', start) : start();
})();
